import React, {useRef, useState} from 'react';
import {
    faTrashAlt,
    faAlignLeft,
    faCopy,
    faDownload,
    faCode,
    faColumns,
    faEye,
    faTree,
    faFileUpload,
    faCompressAlt,
    faGripLines,
    faExpandAlt,
} from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import emoji from 'remark-gemoji';

import ToolBar from '../../../UI/ToolBar/ToolBar';
import Buttons from '../../../UI/ToolBar/Buttons';
import UploadFile from '../../../UI/ToolBar/UploadFile';
import CodeEditor from '../../../UI/CodeEditor/CodeEditor';
import useLocalStorage from '../../../Hooks/useLocalStorage';
import JsonEditor from '../JsonEditor/JsonEditor';
import editorTypes from './editorTypes';
import styles from './Editor.module.scss';

const CONTENT_STORAGE_KEY = 'editor-content'
const ACTIVE_TYPE_STORAGE_KEY = 'editor-active-type'
const MARKDOWN_VIEW_STORAGE_KEY = 'editor-markdown-view-mode'
const JSON_ADVANCED_SIZE_STORAGE_KEY = 'editor-json-advanced-size'

// Content used to live under separate keys per tool (markdown-storage,
// json-editor-storage). Folded in here once so switching to the unified page
// doesn't drop anything someone already had saved in a browser.
const LEGACY_STORAGE_KEYS = {markdown: 'markdown-storage', json: 'json-editor-storage'}

function parseStoredContent(raw) {
    try {
        return JSON.parse(raw) || {}
    } catch (e) {
        return {}
    }
}

function withLegacyContent(content) {
    return Object.entries(LEGACY_STORAGE_KEYS).reduce((acc, [type, legacyKey]) => {
        if (acc[type]) return acc
        const legacyValue = window.localStorage.getItem(legacyKey)
        return legacyValue ? {...acc, [type]: legacyValue} : acc
    }, content)
}

const ACCEPT_BY_LANGUAGE = {
    markdown: '.md,.markdown,.txt',
    json: '.json',
    yaml: '.yaml,.yml',
    xml: '.xml',
}

// One button cycles through these in order instead of showing three separate buttons.
const MARKDOWN_VIEW_MODES = ['edit', 'split', 'preview']
const MARKDOWN_VIEW_ICON = {edit: faCode, split: faColumns, preview: faEye}
const MARKDOWN_VIEW_LABEL = {edit: 'Edit only', split: 'Split view', preview: 'Preview only'}

// How much of the JSON editor's height the docked advanced panel takes, as a
// percentage of the total (the main editor gets the rest). Shown as three
// separate buttons (one per size) rather than a single cycling button, since
// picking directly among three fixed options reads clearer than a toggle.
const JSON_ADVANCED_COMPACT_PERCENT = 100 / 3 // original default share
const JSON_ADVANCED_SIZES = [
    {mode: 'compact', label: 'Compact', icon: faCompressAlt, advancedPercent: JSON_ADVANCED_COMPACT_PERCENT},
    {mode: 'balanced', label: 'Balanced', icon: faGripLines, advancedPercent: 55},
    {mode: 'spacious', label: 'Spacious', icon: faExpandAlt, advancedPercent: 100},
]

// Block-level tags react-markdown's `sourcePos` prop tags with a `data-sourcepos`
// start/end line range. Used to find which rendered block corresponds to the
// line being edited, so the preview can scroll to keep it in view.
const SOURCEPOS_BLOCK_SELECTOR = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li', 'blockquote', 'pre', 'table', 'tr', 'hr',
].map(tag => `${tag}[data-sourcepos]`).join(',')

function scrollPreviewToLine(previewEl, line) {
    if (!previewEl) return
    const blocks = previewEl.querySelectorAll(SOURCEPOS_BLOCK_SELECTOR)
    let target = null
    for (const block of blocks) {
        const start = parseInt(block.getAttribute('data-sourcepos'), 10)
        if (Number.isNaN(start) || start > line) break
        target = block
    }
    if (!target) return
    const containerTop = previewEl.getBoundingClientRect().top
    const targetTop = target.getBoundingClientRect().top
    const offset = targetTop - containerTop + previewEl.scrollTop
    previewEl.scrollTop = Math.max(offset - 12, 0)
}

export default function Editor() {
    let [storedContent, setStoredContent] = useLocalStorage(CONTENT_STORAGE_KEY)
    let [storedActiveTypeKey, setStoredActiveTypeKey] = useLocalStorage(ACTIVE_TYPE_STORAGE_KEY)
    let [storedMarkdownView, setStoredMarkdownView] = useLocalStorage(MARKDOWN_VIEW_STORAGE_KEY)
    let [storedJsonAdvancedSize, setStoredJsonAdvancedSize] = useLocalStorage(JSON_ADVANCED_SIZE_STORAGE_KEY)
    let [jsonAdvanced, setJsonAdvanced] = useState(false)
    let [copied, setCopied] = useState(false)
    let [formatError, setFormatError] = useState('')
    const previewRef = useRef()

    const contentByType = withLegacyContent(parseStoredContent(storedContent))
    const activeType = editorTypes.find(t => t.key === storedActiveTypeKey) || editorTypes[0]
    const markdownViewMode = storedMarkdownView || 'split'
    const jsonAdvancedSize = storedJsonAdvancedSize || 'compact'
    const jsonAdvancedPercent = (JSON_ADVANCED_SIZES.find(s => s.mode === jsonAdvancedSize) || JSON_ADVANCED_SIZES[0]).advancedPercent
    const jsonMainPercent = 100 - jsonAdvancedPercent
    const value = contentByType[activeType.key] || ''

    const handleContentChange = (newValue) => {
        setStoredContent(JSON.stringify({...contentByType, [activeType.key]: newValue}))
        setFormatError('')
    }

    const handleTypeChange = (key) => {
        setStoredActiveTypeKey(key)
        setFormatError('')
    }

    const handleReset = () => handleContentChange('')

    const handleLoadSample = () => handleContentChange(activeType.placeholder)

    const handleFormat = () => {
        if (!activeType.format) return
        try {
            handleContentChange(activeType.format(value))
        } catch (err) {
            setFormatError(err.message || 'Unable to format')
        }
    }

    const handleCopy = () => {
        if (!value) return
        navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    const handleDownload = () => {
        if (!value) return
        const blob = new Blob([value], {type: activeType.mimeType})
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `edited.${activeType.extension}`
        link.click()
        URL.revokeObjectURL(url)
    }

    const handleMarkdownCursorLine = (line) => {
        if (markdownViewMode === 'split') scrollPreviewToLine(previewRef.current, line)
    }

    const handleCycleMarkdownView = () => {
        const next = MARKDOWN_VIEW_MODES[(MARKDOWN_VIEW_MODES.indexOf(markdownViewMode) + 1) % MARKDOWN_VIEW_MODES.length]
        setStoredMarkdownView(next)
    }

    const handleSetJsonAdvancedSize = (mode) => setStoredJsonAdvancedSize(mode)

    const handleUpload = (event) => {
        const file = event.target.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onloadend = () => handleContentChange(reader.result)
        reader.readAsText(file)
    }

    const typeButtons = editorTypes.map(t => ({
        tip: t.label,
        text: t.label,
        clickHandler: () => handleTypeChange(t.key),
        active: t.key === activeType.key,
    }))

    const primaryActionButtons = [
        {tip: 'Load sample data', clickHandler: handleLoadSample, text: 'Sample'},
        {tip: 'Reset', clickHandler: handleReset, icon: faTrashAlt},
    ]

    const markdownViewButtons = activeType.key === 'markdown' ? [
        {
            tip: `View: ${MARKDOWN_VIEW_LABEL[markdownViewMode]} (click to switch)`,
            clickHandler: handleCycleMarkdownView,
            icon: MARKDOWN_VIEW_ICON[markdownViewMode],
        },
    ] : []

    const jsonAdvancedButtons = activeType.key === 'json' ? [
        {
            tip: jsonAdvanced ? 'Hide advanced JSON tools' : 'Advanced JSON tools (tree view, schema validation)',
            clickHandler: () => setJsonAdvanced(a => !a),
            icon: faTree,
            active: jsonAdvanced,
        },
    ] : []

    const formatButtons = activeType.format ? [
        {tip: 'Format', clickHandler: handleFormat, icon: faAlignLeft},
    ] : []

    const outputActionButtons = [
        {tip: copied ? 'Copied!' : 'Copy to clipboard', clickHandler: handleCopy, icon: faCopy},
        {tip: 'Download file', clickHandler: handleDownload, icon: faDownload},
    ]

    return (
        <div className={styles.main}>
            <ToolBar>
                <div className={styles.toolbarRow}>
                    <Buttons buttons={typeButtons}/>
                    <div className={styles.toolbarRow} style={{justifyContent: 'flex-end'}}>
                        <Buttons buttons={primaryActionButtons}/>
                        <UploadFile id={'file-input-editor'} icon={faFileUpload} handler={handleUpload}
                                    tip={'Upload file'} accept={ACCEPT_BY_LANGUAGE[activeType.language]}/>
                        <Buttons buttons={markdownViewButtons}/>
                        <Buttons buttons={jsonAdvancedButtons}/>
                        <Buttons buttons={formatButtons}/>
                        <Buttons buttons={outputActionButtons}/>
                    </div>
                </div>
            </ToolBar>
            {formatError && <div className={styles.error}>{formatError}</div>}
            <div className={styles.body}>
                {activeType.key === 'markdown' && (
                    <div className={styles.markdownBody}>
                        {markdownViewMode !== 'preview' && (
                            <div className={markdownViewMode === 'split' ? styles.editorPaneBordered : styles.editorPane}>
                                <CodeEditor language="markdown" value={value} onChange={handleContentChange}
                                            onCursorLine={handleMarkdownCursorLine}/>
                            </div>
                        )}
                        {markdownViewMode !== 'edit' && (
                            <div className={styles.preview} ref={previewRef}>
                                <ReactMarkdown children={value} remarkPlugins={[remarkGfm, emoji]} sourcePos/>
                            </div>
                        )}
                    </div>
                )}
                {activeType.key === 'json' && (
                    <div className={styles.jsonBody}>
                        <div className={styles.pane} style={jsonAdvanced ? {flex: `0 0 ${jsonMainPercent}%`} : undefined}>
                            <CodeEditor language="json" value={value} onChange={handleContentChange}/>
                        </div>
                        {jsonAdvanced && (
                            <div className={styles.jsonAdvancedPane} style={{flex: `0 0 ${jsonAdvancedPercent}%`}}>
                                <JsonEditor value={value} onChange={handleContentChange}
                                            onClose={() => setJsonAdvanced(false)}
                                            sizeOptions={JSON_ADVANCED_SIZES.map(s => ({
                                                mode: s.mode,
                                                tip: `${s.label} panel size`,
                                                icon: s.icon,
                                                active: s.mode === jsonAdvancedSize,
                                            }))}
                                            onSetSize={handleSetJsonAdvancedSize}/>
                            </div>
                        )}
                    </div>
                )}
                {(activeType.key === 'yaml' || activeType.key === 'xml') && (
                    <CodeEditor language={activeType.language} value={value} onChange={handleContentChange}/>
                )}
            </div>
        </div>
    );
}
